

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Idea, UserProfile, PlanTier, PLAN_LIMITS, MetricEntry, MonthlyAllowance } from '../types';
import { auth, googleProvider } from '../services/firebase';
import { signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { 
    getIdeas, getPublicIdeas, saveIdea as saveIdeaService, deleteIdea as deleteIdeaService, 
    archiveIdea as archiveIdeaService, syncGuestIdeasToFirebase,
    getUserProfile, updateUserProfile, incrementUsage, getMonthlyAllowance, upgradeUserPlan
} from '../services/storage';
import { MODEL_FLASH, MODEL_PRO } from '../services/geminiService';

const safeLocalStorage = {
    getItem: (key: string) => { try { return localStorage.getItem(key); } catch (e) { return null; } },
    setItem: (key: string, value: string) => { try { localStorage.setItem(key, value); } catch (e) {} },
    removeItem: (key: string) => { try { localStorage.removeItem(key); } catch (e) {} }
};

interface AppState {
  user: User | null;
  userProfile: UserProfile | null;
  allowance: MonthlyAllowance | null;
  isGuest: boolean;
  isAuthLoading: boolean; 
  authError: string | null;
  isSyncing: boolean;

  ideas: Idea[];
  publicIdeas: Idea[];
  isLoadingIdeas: boolean;

  selectedIdea: Idea | null;
  comparisonIds: string[];
  isPricingOpen: boolean;

  initializeAuth: () => void;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  enterGuestMode: () => void;
  
  loadIdeas: () => Promise<void>;
  loadPublicIdeas: () => Promise<void>;
  saveIdea: (idea: Idea, isNew?: boolean) => Promise<void>;
  deleteIdea: (id: string) => Promise<void>;
  setIdeaArchiveStatus: (id: string, isArchived: boolean) => Promise<void>;
  toggleIdeaPrivacy: (idea: Idea) => Promise<void>;
  
  consumeTokens: (amount: number, model: string) => Promise<void>;
  upgradeSubscription: (plan: PlanTier) => Promise<void>;
  checkIdeaLimit: () => boolean;
  checkTokenLimit: (cost: number) => boolean;
  getBestModel: () => string;
  canGenerateImages: () => boolean;
  
  addMetricEntry: (ideaId: string, entry: MetricEntry) => Promise<void>;
  deleteMetricEntry: (ideaId: string, entryId: string) => Promise<void>;
  completeOnboarding: (role: string, company: string) => Promise<void>;

  selectIdea: (idea: Idea | null) => void;
  setComparisonIds: (ids: string[]) => void;
  setPricingOpen: (isOpen: boolean) => void;
}

export const useStore = create<AppState>()(
  devtools((set, get) => ({
    user: null,
    userProfile: null,
    allowance: null,
    isGuest: safeLocalStorage.getItem('idea_shelf_auth_mode') === 'guest',
    isAuthLoading: safeLocalStorage.getItem('idea_shelf_auth_mode') === 'user',
    authError: null,
    isSyncing: false,
    
    ideas: [],
    publicIdeas: [],
    isLoadingIdeas: false,
    
    selectedIdea: null,
    comparisonIds: [],
    isPricingOpen: false,

    initializeAuth: () => {
      onAuthStateChanged(auth, async (currentUser) => {
        if (currentUser) {
          safeLocalStorage.setItem('idea_shelf_auth_mode', 'user');
          set({ user: currentUser, isGuest: false });
          
          try {
              // Parallel fetch for speed
              const profile = await getUserProfile(currentUser.uid, currentUser.email || 'user@example.com');
              set({ userProfile: profile }); 
              
              const allowance = await getMonthlyAllowance(currentUser.uid);
              set({ allowance });
          } catch (e) {
              console.error("Failed to load profile", e);
          } finally {
              set({ isAuthLoading: false });
          }
          
          get().loadIdeas();
          
          set({ isSyncing: true });
          syncGuestIdeasToFirebase(currentUser)
            .then(() => get().loadIdeas())
            .catch(e => console.error("Sync failed", e))
            .finally(() => set({ isSyncing: false }));

        } else {
          if (safeLocalStorage.getItem('idea_shelf_auth_mode') === 'user') {
             safeLocalStorage.removeItem('idea_shelf_auth_mode');
          }

          set(state => ({ 
             user: null, 
             userProfile: null,
             allowance: null,
             isAuthLoading: false,
             ideas: state.isGuest ? state.ideas : [] 
          }));
          
          if (get().isGuest) {
              get().loadIdeas();
              set({ userProfile: { 
                  uid: 'guest', email: 'guest', plan: 'free', 
                  ideasCreated: get().ideas.length, tokensUsed: 0, 
                  subscriptionStatus: 'active', periodEnd: 0 
              }});
          }
        }
      });
    },

    login: async () => {
      set({ isAuthLoading: true, authError: null });
      try {
        await signInWithPopup(auth, googleProvider);
      } catch (error: any) {
        console.error("Login failed", error);
        let msg = "Login failed.";
        if (error.code === 'auth/unauthorized-domain') {
          msg = `Domain not authorized. Add "${window.location.hostname}" to Firebase Console.`;
        }
        set({ authError: msg, isAuthLoading: false });
      }
    },

    logout: async () => {
      await signOut(auth);
      safeLocalStorage.removeItem('idea_shelf_auth_mode');
      set({ 
          user: null, 
          userProfile: null,
          allowance: null,
          isGuest: false, 
          ideas: [], 
          selectedIdea: null,
          isAuthLoading: false
      });
    },

    enterGuestMode: () => {
      safeLocalStorage.setItem('idea_shelf_auth_mode', 'guest');
      set({ 
          isGuest: true, 
          userProfile: { 
              uid: 'guest', email: 'guest', plan: 'free', 
              ideasCreated: 0, tokensUsed: 0, 
              subscriptionStatus: 'active', periodEnd: 0 
          }
      });
      get().loadIdeas();
    },

    loadIdeas: async () => {
      if (get().ideas.length === 0) set({ isLoadingIdeas: true });
      try {
        const ideas = await getIdeas();
        // Don't filter out archived ideas here, let the UI handle visibility
        set({ ideas: ideas });
        if (get().isGuest) {
            set(state => state.userProfile ? ({ 
                userProfile: { ...state.userProfile, ideasCreated: ideas.length } 
            }) : {});
        }
      } catch (e) { console.error(e); } finally { set({ isLoadingIdeas: false }); }
    },

    loadPublicIdeas: async () => {
      set({ isLoadingIdeas: true });
      try {
        const ideas = await getPublicIdeas();
        set({ publicIdeas: ideas });
      } catch (e) { console.error(e); } finally { set({ isLoadingIdeas: false }); }
    },

    checkIdeaLimit: () => {
        const { userProfile, allowance, ideas } = get();
        
        // Priority: Allowance Plan > Profile Plan > Free
        const planKey = allowance?.plan || userProfile?.plan || 'free';
        // Safe lookup
        const plan = PLAN_LIMITS[planKey] ? planKey : 'free';
        const limit = PLAN_LIMITS[plan].ideas;
        
        console.group("💡 IDEA LIMIT CHECK");
        console.log(`Current Plan: ${plan}`);
        console.log(`Active Ideas: ${ideas.filter(i => !i.isArchived).length}`);
        console.log(`Plan Limit: ${limit}`);
        
        if (ideas.filter(i => !i.isArchived).length >= limit) {
            console.warn("❌ Limit Reached. Denying creation.");
            console.groupEnd();
            set({ isPricingOpen: true });
            return false;
        }
        console.log("✅ Check Passed");
        console.groupEnd();
        return true;
    },

    checkTokenLimit: (cost: number) => {
        const { userProfile, allowance } = get();
        
        console.group("💰 TOKEN LIMIT CHECK");
        console.log(`Requested Cost: ${cost} credits`);
        
        // 1. Check Monthly Allowance Doc (Primary Source of Truth for Logged In Users)
        if (allowance) {
            const projectedUsage = allowance.used + cost;
            const remaining = allowance.limit - allowance.used;
            
            console.log(`Active Plan (from Allowance): ${allowance.plan}`);
            console.log(`Monthly Limit: ${allowance.limit}`);
            console.log(`Used so far: ${allowance.used}`);
            console.log(`Remaining: ${remaining}`);
            console.log(`Projected Usage: ${projectedUsage}`);

            if (projectedUsage > allowance.limit) {
                console.warn("❌ Limit Exceeded. Opening pricing modal.");
                console.groupEnd();
                set({ isPricingOpen: true });
                return false;
            }
            console.log("✅ Allowed.");
            console.groupEnd();
            return true;
        }

        // 2. Fallback for Guests or if allowance fails to load
        const plan = userProfile?.plan && PLAN_LIMITS[userProfile.plan] ? userProfile.plan : 'free';
        const limitConfig = PLAN_LIMITS[plan];
        const used = userProfile?.tokensUsed || 0;
        
        console.log("⚠️ Fallback Mode (Guest/No Allowance)");
        console.log(`Effective Plan: ${plan}`);
        console.log(`Total Lifetime Used: ${used}`);
        console.log(`Limit: ${limitConfig.credits}`);

        if (used + cost > limitConfig.credits) {
             console.warn("❌ Limit Exceeded (Fallback). Opening pricing modal.");
             console.groupEnd();
             set({ isPricingOpen: true });
             return false;
        }
        console.log("✅ Allowed (Fallback).");
        console.groupEnd();
        return true;
    },

    getBestModel: () => {
        const { userProfile, allowance } = get();
        if (!userProfile) return MODEL_FLASH;
        
        const planKey = allowance?.plan || userProfile.plan || 'free';
        const plan = PLAN_LIMITS[planKey] ? planKey : 'free';
        const limits = PLAN_LIMITS[plan];
        
        return limits.models.includes('pro') ? MODEL_PRO : MODEL_FLASH;
    },

    canGenerateImages: () => {
        const { userProfile, allowance } = get();
        if (!userProfile) return false;
        
        const planKey = allowance?.plan || userProfile.plan || 'free';
        const plan = PLAN_LIMITS[planKey] ? planKey : 'free';
        
        return PLAN_LIMITS[plan].imageGeneration;
    },

    consumeTokens: async (amount: number, model: string) => {
        const { user, isGuest } = get();
        
        // Calculate Weighted Cost
        // Flash models = 1x
        // Pro/Image models = 10x
        let multiplier = 1;
        if (model.includes('pro') || model.includes('image')) {
            multiplier = 10;
        }
        const cost = amount * multiplier;
        
        console.log(`Spending ${cost} credits (Base: ${amount}, Multiplier: ${multiplier}x)`);

        if (user) {
            await incrementUsage(user.uid, 'tokens', cost);
            
            // Optimistic update of Allowance
            set(state => {
                const newAllowance = state.allowance ? { ...state.allowance, used: state.allowance.used + cost } : null;
                const newProfile = state.userProfile ? { ...state.userProfile, tokensUsed: state.userProfile.tokensUsed + cost } : null;
                return { userProfile: newProfile, allowance: newAllowance };
            });
        } else if (isGuest) {
            set(state => state.userProfile ? ({ 
                userProfile: { ...state.userProfile, tokensUsed: state.userProfile.tokensUsed + cost } 
            }) : {});
        }
    },

    upgradeSubscription: async (plan: PlanTier) => {
        const { user } = get();
        if (!user) return alert("Please log in to upgrade.");
        
        // Use the new atomic upgrade function in storage
        // This updates BOTH profile and monthly allowance limits
        const newAllowance = await upgradeUserPlan(user.uid, plan);

        set(state => state.userProfile ? ({ 
            userProfile: { ...state.userProfile, plan },
            allowance: newAllowance
        }) : {});
        
        set({ isPricingOpen: false });
        alert(`Successfully upgraded to ${PLAN_LIMITS[plan].label}!`);
    },

    saveIdea: async (idea: Idea, isNew: boolean = false) => {
      const { userProfile, user, allowance } = get();
      if (user && !idea.userId) idea.userId = user.uid;
      
      const planKey = allowance?.plan || userProfile?.plan || 'free';
      const plan = PLAN_LIMITS[planKey] ? planKey : 'free';

      if (userProfile && !PLAN_LIMITS[plan].privateIdeas) {
          idea.isPublic = true;
      }
      
      if (isNew && !get().checkIdeaLimit()) return;

      const prevIdeas = get().ideas;
      const existingIdx = prevIdeas.findIndex(i => i.id === idea.id);
      let newIdeas = [...prevIdeas];
      if (existingIdx >= 0) newIdeas[existingIdx] = idea;
      else newIdeas = [idea, ...prevIdeas];
      set({ ideas: newIdeas });
      if (get().selectedIdea?.id === idea.id) set({ selectedIdea: idea });

      try {
        await saveIdeaService(idea);
        if (isNew && !get().isGuest && get().user) {
            await incrementUsage(get().user!.uid, 'ideas', 1);
            set(state => state.userProfile ? ({ 
                userProfile: { ...state.userProfile, ideasCreated: state.userProfile.ideasCreated + 1 } 
            }) : {});
        }
      } catch (e) {
        console.error("Save failed", e);
        set({ ideas: prevIdeas });
        throw e;
      }
    },

    addMetricEntry: async (ideaId: string, entry: MetricEntry) => {
        const { ideas, saveIdea } = get();
        const idea = ideas.find(i => i.id === ideaId);
        if (!idea) return;
        const updatedIdea = { ...idea, metrics: [...(idea.metrics || []), entry] };
        await saveIdea(updatedIdea);
    },

    deleteMetricEntry: async (ideaId: string, entryId: string) => {
        const { ideas, saveIdea } = get();
        const idea = ideas.find(i => i.id === ideaId);
        if (!idea || !idea.metrics) return;
        const updatedIdea = { ...idea, metrics: idea.metrics.filter(m => m.id !== entryId) };
        await saveIdea(updatedIdea);
    },

    deleteIdea: async (id: string) => {
      const prevIdeas = get().ideas;
      set({ ideas: prevIdeas.filter(i => i.id !== id) });
      try { await deleteIdeaService(id); } catch (e) { set({ ideas: prevIdeas }); throw e; }
    },

    setIdeaArchiveStatus: async (id: string, isArchived: boolean) => {
      const prevIdeas = get().ideas;
      const newIdeas = prevIdeas.map(i => i.id === id ? { ...i, isArchived } : i);
      set({ ideas: newIdeas });
      
      // Update selected idea if it's the one being modified
      const selected = get().selectedIdea;
      if (selected && selected.id === id) {
          set({ selectedIdea: { ...selected, isArchived } });
      }

      try { 
          await archiveIdeaService(id, isArchived); 
      } catch (e) { 
          set({ ideas: prevIdeas }); 
          throw e; 
      }
    },

    toggleIdeaPrivacy: async (idea: Idea) => {
        const { userProfile, allowance, saveIdea } = get();
        if (!userProfile) return;
        
        const planKey = allowance?.plan || userProfile.plan || 'free';
        const plan = PLAN_LIMITS[planKey] ? planKey : 'free';

        if (idea.isPublic && !PLAN_LIMITS[plan].privateIdeas) {
            set({ isPricingOpen: true });
            return;
        }
        const updated = { ...idea, isPublic: !idea.isPublic };
        await saveIdea(updated);
    },

    completeOnboarding: async (role: string, company: string) => {
        const { user } = get();
        if (!user) return;
        const updates = { onboardingCompleted: true, role, company };
        set(state => state.userProfile ? ({ userProfile: { ...state.userProfile, ...updates } }) : {});
        try { await updateUserProfile(user.uid, updates); } catch(e) { console.error(e); }
    },

    selectIdea: (idea) => set({ selectedIdea: idea }),
    setComparisonIds: (ids) => set({ comparisonIds: ids }),
    setPricingOpen: (isOpen) => set({ isPricingOpen: isOpen }),

  }))
);